import React, { useRef, useState } from 'react';
import { Button, LayoutProvider, TextInput } from '@mayfield/common/components';
import { regex } from '@mayfield/common/constants';
import { ICentreContactForm, TThemeColor } from '@mayfield/sanity';
import cn from 'classnames';
import * as styles from './styles.module.scss';

interface IFormValue {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

interface IFormError {
  firstName: boolean;
  lastName: boolean;
  email: boolean;
  phone: boolean;
  message: boolean;
}

type TSubmissionStatus = 'idle' | 'submitting' | 'completed' | 'error';

interface IProps extends ICentreContactForm {
  color: TThemeColor;
  centreTitle: string;
  databaseID: string;
}

const Enquire = ({
  color,
  centreTitle,
  buttonCTA,
  header,
  subheader,
  successMessage,
  databaseID
}: IProps) => {
  const initialFormValue: IFormValue = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  };

  const initialFormErrors: IFormError = {
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    message: false
  };

  const [formValue, setFormValue] = useState(initialFormValue);
  const [formErrors, setFormErrors] = useState(initialFormErrors);
  const [submissionStatus, setSubmissionStatus] =
    useState<TSubmissionStatus>('idle');

  const containerRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (field: keyof IFormValue, value: string) => {
    setFormValue((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: false }));
  };

  const mapFieldToRegex = (field: keyof IFormValue) => {
    switch (field) {
      case 'email':
        return regex.email;
      case 'phone':
        return regex.phone;
      default:
        return regex.basicText;
    }
  };

  const validateField = (field: keyof IFormValue) => {
    const fieldRegex = mapFieldToRegex(field);
    const fieldValue = formValue[field];

    const isFieldValid = fieldValue && fieldRegex.test(fieldValue);
    const fieldHasError = !isFieldValid;

    setFormErrors((prev) => ({ ...prev, [field]: fieldHasError }));
  };

  const handleBlur = (field: keyof IFormValue) => {
    validateField(field);
  };

  const areAllFieldsPopulated: () => boolean = () => {
    const formValues = Object.values(formValue);
    const isAnyFieldEmpty = formValues.some((value) => !value);

    if (isAnyFieldEmpty) {
      return false;
    }

    return true;
  };

  const getIsSubmitEnabled = () => {
    if (!areAllFieldsPopulated()) {
      return false;
    }

    const formErrorKeys = Object.keys(formErrors) as Array<keyof IFormError>;
    const hasFormError = formErrorKeys.some((key) => formErrors[key]);

    if (hasFormError) {
      return false;
    }

    return true;
  };

  const isSubmitDisabled = !getIsSubmitEnabled();

  const scrollToTopOfSection = () => {
    const containerDistanceFromTopOfScreen =
      containerRef.current?.getBoundingClientRect().top;
    const containerDistanceFromTopOfPage =
      window.scrollY + (containerDistanceFromTopOfScreen || 0);
    window.scrollTo({
      top: containerDistanceFromTopOfPage - 112,
      behavior: 'smooth'
    }); // 112px is the height of the header + navbar
  };

  const pushSubmissionDataLayerEvent = () => {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'centre_enquiry',
      centre: centreTitle
    });
  };

  const handleSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitDisabled) return; // Just in case

    setSubmissionStatus('submitting');

    const formData = new FormData();
    formData.append('first_name', formValue.firstName);
    formData.append('last_name', formValue.lastName);
    formData.append('email', formValue.email);
    formData.append('phone', formValue.phone);
    formData.append('comments', formValue.message);
    formData.append('location', `Mayfield Early Education ${centreTitle}`);
    formData.append('hash', databaseID);

    const request = {
      method: 'POST',
      body: formData
    };

    // Server doesn't send a response, so will always error
    try {
      await fetch(
        'https://live.childcarecrm.com.au/import/webImportReceiver.php',
        request
      );
    } catch (error) {
      pushSubmissionDataLayerEvent();
      scrollToTopOfSection();
      setSubmissionStatus('completed');
    }
  };

  const hasSubmitted = submissionStatus === 'completed';
  const isSubmitting = submissionStatus === 'submitting';
  const hasSubmissionError = submissionStatus === 'error';

  const headerText = hasSubmitted ? successMessage : subheader;

  return (
    <LayoutProvider maxWidth padding grid>
      <div
        className={styles.container}
        ref={containerRef}
        id="centreContactForm"
        style={{ color: `var(--color-${color}-dark)` }}
      >
        <h2 className={cn('h2', styles.title)}>{header}</h2>
        <p className={cn('b1', styles.headerText)}>{headerText}</p>

        {!hasSubmitted && (
          <form name="centreContactForm" onSubmit={handleSubmitForm}>
            <div className={styles.inlineFields}>
              <TextInput
                className={styles.input}
                disabled={isSubmitting}
                themeColor={color}
                hasError={formErrors.firstName}
                onBlur={() => handleBlur('firstName')}
                label="First Name"
                placeholder="Enter name"
                required
                value={formValue.firstName}
                onChange={(value) => handleInputChange('firstName', value)}
              />

              <TextInput
                className={styles.input}
                disabled={isSubmitting}
                themeColor={color}
                hasError={formErrors.lastName}
                onBlur={() => handleBlur('lastName')}
                label="Last Name"
                placeholder="Enter name"
                required
                value={formValue.lastName}
                onChange={(value) => handleInputChange('lastName', value)}
              />
            </div>

            <TextInput
              className={styles.input}
              disabled={isSubmitting}
              themeColor={color}
              hasError={formErrors.email}
              onBlur={() => handleBlur('email')}
              label="Email"
              placeholder="Enter email"
              required
              value={formValue.email}
              onChange={(value) => handleInputChange('email', value)}
            />
            <TextInput
              className={styles.input}
              disabled={isSubmitting}
              themeColor={color}
              hasError={formErrors.phone}
              onBlur={() => handleBlur('phone')}
              label="Phone"
              placeholder="Enter phone number"
              required
              value={formValue.phone}
              onChange={(value) => handleInputChange('phone', value)}
            />
            <TextInput
              className={styles.input}
              disabled={isSubmitting}
              themeColor={color}
              placeholder="Enter your message"
              hasError={formErrors.message}
              onBlur={() => handleBlur('message')}
              label="Message"
              required
              value={formValue.message}
              textarea
              onChange={(value) => handleInputChange('message', value)}
            />

            <Button
              color={color}
              buttonType="submit"
              disabled={isSubmitDisabled}
              loading={isSubmitting}
            >
              {buttonCTA}
            </Button>

            {hasSubmissionError && (
              <p className={cn('caption', styles.errorMessage)}>
                Something went wrong, please try again in a few minutes
              </p>
            )}
          </form>
        )}
      </div>
    </LayoutProvider>
  );
};

export default Enquire;
